import { Skeleton } from '@morpho-org/ui-v6/server'
import { cn } from '@morpho-org/ui-v6/utils'
import { Icon, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import { Alert, ArrowRight } from '@repo/ui/icons'
import { type CSSProperties, memo } from 'react'

/**
 * @example
 * ```tsx
 * <ValueTransition
 *   initial={{
 *     value: "100 USDC",
 *     tooltip: "Current balance",
 *     colorClass: "text-foreground"
 *   }}
 *   final={{
 *     value: "150 USDC",
 *     tooltip: "After transaction",
 *     colorClass: "text-foreground"
 *   }}
 *   loading={{
 *     isLoading: false,
 *     isFetchingFinalValue: false
 *   }}
 *   config={{
 *     showFinalValue: true,
 *     showErrorIcon: false,
 *     testId: "balance-transition"
 *   }}
 * />
 * ```
 */

const LoadingSkeleton = ({ width = '30px' }: { width?: string }) => (
  <Skeleton className="h-[14px] rounded-[20px]" style={{ width }} />
)

const ValueWithTooltip = ({
  value,
  tooltipContent,
  colorClass = 'text-foreground',
  disabled = false,
  lineHeight,
  testId
}: {
  value?: string | null
  tooltipContent?: string
  colorClass?: string
  disabled?: boolean
  lineHeight?: CSSProperties['lineHeight']
  testId?: string
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span data-testid={testId} className={cn('text-3xs', colorClass)} style={{ lineHeight }}>
        {value}
      </span>
    </TooltipTrigger>
    {!disabled && <TooltipContent>{tooltipContent}</TooltipContent>}
  </Tooltip>
)

interface ValueConfig {
  value?: string | null
  tooltip?: string
  colorClass: string
}

interface LoadingState {
  isLoading?: boolean
  isFetchingFinalValue?: boolean
}

interface TransitionConfig {
  showFinalValue: boolean
  showErrorIcon?: boolean
  testId?: string
  arrowColorClass?: string
}

const ValueTransition = ({
  initial,
  final,
  loading,
  config
}: {
  initial: ValueConfig
  final?: Partial<ValueConfig>
  loading?: LoadingState
  config: TransitionConfig
}) => (
  <div className="flex items-center gap-[2px]">
    {loading?.isLoading ? (
      <LoadingSkeleton />
    ) : (
      <ValueWithTooltip
        testId={`${config.showFinalValue ? 'initial-' : 'final-'}${config.testId}`}
        value={initial.value}
        tooltipContent={initial.tooltip}
        colorClass={initial.colorClass}
        disabled={!initial.tooltip}
        lineHeight="normal"
      />
    )}

    {config.showFinalValue && (
      <div className="animate-bounce-from-left flex items-center gap-[2px]">
        <Icon icon={<ArrowRight />} className={config.arrowColorClass ?? 'text-muted-foreground'} />
        {loading?.isFetchingFinalValue ? (
          <LoadingSkeleton />
        ) : (
          <>
            <ValueWithTooltip
              testId={`final-${config.testId}`}
              value={final?.value || ''}
              tooltipContent={final?.tooltip}
              colorClass={final?.colorClass || 'text-foreground'}
              disabled={!final?.tooltip}
              lineHeight="normal"
            />

            {config.showErrorIcon && (
              <Icon className="text-destructive-foreground" icon={<Alert />} />
            )}
          </>
        )}
      </div>
    )}
  </div>
)

export default memo(ValueTransition)
